import {
  Document,
  Font,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer'
import dayjs from 'dayjs'
import JsBarcode from 'jsbarcode'
import QRCode from 'qrcode'

import SarabunFontBold from '../../../assets/fonts/th-sarabun-bold.ttf'
import SarabunFont from '../../../assets/fonts/th-sarabun-new.ttf'
import { ONLY_DATE_TIME_FORMAT } from '../../../constants/global'
import { IFlashTrackingLabel } from '../../../types/flash'
import { EOrderType, IOrder, IOrderItem } from '../../../types/order'
import { numberFormat } from '../../../utils'

// Own family so the label's sizes are not affected by the other PDFs that
// register different files under 'Sarabun'.
Font.register({ family: 'THSarabunNew', src: SarabunFont })
Font.register({
  family: 'THSarabunNew',
  src: SarabunFontBold,
  fontWeight: 'bold',
})

export type FlashLabelSize = 'small' | 'large'

interface IPDFFlashProps {
  orders: IOrder[]
  labels: Record<string, IFlashTrackingLabel>
  size: FlashLabelSize
  showFullPhone?: boolean
  showItemInPack?: boolean
  showItemAsTable?: boolean
}

const MM = 72 / 25.4
const PAGE_SIZES: Record<FlashLabelSize, [number, number]> = {
  small: [100 * MM, 75 * MM],
  large: [100 * MM, 180 * MM],
}

// Glyph heights match Flash's own pre_print label, which uses Tahoma
// (digits 0.73em tall). THSarabunNew-Bold digits are 0.48em tall, so the same
// height needs ~1.5x the point size. react-pdf also puts the baseline
// 0.7em below the top of the line box whatever the lineHeight, leaving empty
// space above big digits. The sort code sits in a fixed-height box and is
// pulled up with absolute positioning so that space does not push the label
// past the 100x75 page.
const borderColor = '#000'
const styles = StyleSheet.create({
  page: {
    fontFamily: 'THSarabunNew',
    paddingHorizontal: 6,
    paddingTop: 3,
    paddingBottom: 3,
  },
  sortCode: { height: 20 },
  sortCodeParts: {
    position: 'absolute',
    top: -13,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontWeight: 'bold',
  },
  sortCodeSmall: { fontSize: 31, lineHeight: 0.9 },
  sortCodeLarge: { fontSize: 38, lineHeight: 0.9 },
  barcode: { alignItems: 'center', marginTop: 1 },
  pno: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 1,
    marginBottom: 1,
  },
  box: { borderWidth: 1, borderColor },
  dst: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    borderBottomWidth: 1,
    borderColor,
    paddingHorizontal: 3,
  },
  dstLabel: { fontSize: 14, marginRight: 4, lineHeight: 1 },
  dstName: { fontSize: 12, fontWeight: 'bold', lineHeight: 1.1 },
  body: { flexDirection: 'row' },
  main: { flex: 1, justifyContent: 'space-between' },
  addresses: { paddingHorizontal: 3, paddingBottom: 2 },
  sender: { fontSize: 11, lineHeight: 1, textOverflow: 'ellipsis' },
  recipientName: {
    fontWeight: 'bold',
    lineHeight: 1,
    marginTop: 2,
  },
  recipientAddress: { fontWeight: 'bold', lineHeight: 1 },
  labelNote: {
    fontSize: 10,
    lineHeight: 1,
    marginTop: 2,
    textOverflow: 'ellipsis',
  },
  side: {
    width: 84,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 2,
  },
  sortingLine: {
    width: 80,
    height: 46,
    backgroundColor: '#000',
    color: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sortingLineText: {
    fontSize: 48,
    fontWeight: 'bold',
    lineHeight: 0.9,
    marginTop: -6,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderColor,
    minHeight: 20,
  },
  cod: {
    borderRightWidth: 1,
    borderColor,
    paddingHorizontal: 4,
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  codText: { fontSize: 16, fontWeight: 'bold', lineHeight: 1 },
  orderInfo: { flex: 1, paddingHorizontal: 4 },
  orderCode: { fontSize: 12, fontWeight: 'bold', lineHeight: 1 },
  printedAt: { fontSize: 9, lineHeight: 1, marginTop: 1 },
  note: { fontSize: 12, lineHeight: 1, marginTop: 3 },
  continuation: {
    borderBottomWidth: 1,
    borderColor,
    marginBottom: 4,
    paddingBottom: 2,
  },
  // Text with a render prop skips style inheritance and the 'bold' → 700
  // conversion, so the font is set here with a numeric weight.
  continuationTitle: {
    fontFamily: 'THSarabunNew',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  continuationRef: { fontFamily: 'THSarabunNew', fontSize: 11, lineHeight: 1 },
})

const tableStyles = StyleSheet.create({
  table: { marginTop: 4 },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor,
  },
  headerRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor,
  },
  header: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 2,
    lineHeight: 1,
  },
  cell: { fontSize: 12, padding: 2, lineHeight: 1 },
  cellBorder: { borderLeftWidth: 1, borderColor },
  index: { width: '9%', textAlign: 'center' },
  code: { width: '24%' },
  name: { width: '53%' },
  quantity: { width: '14%', textAlign: 'center' },
})

function BarCode({ value }: { value: string }) {
  const barcode: { encodings?: { data: string }[] } = {}
  JsBarcode(barcode, value, { format: 'CODE128' })
  const bits = (barcode.encodings ?? []).map((e) => e.data).join('')
  const quietZone = 10
  let path = ''
  for (let x = 0; x < bits.length; x++) {
    if (bits[x] === '1') {
      path += `M${x} 0h1v1h-1z`
    }
  }
  return (
    <Svg
      width={200}
      height={34.5}
      viewBox={`${-quietZone} 0 ${bits.length + quietZone * 2} 1`}
      preserveAspectRatio="none"
    >
      <Path d={path} fill="#000" />
    </Svg>
  )
}

function QrCode({ value, size }: { value: string; size: number }) {
  const { modules } = QRCode.create(value, { errorCorrectionLevel: 'M' })
  const quietZone = 1
  let path = ''
  for (let row = 0; row < modules.size; row++) {
    for (let col = 0; col < modules.size; col++) {
      if (modules.get(row, col)) {
        path += `M${col} ${row}h1v1h-1z`
      }
    }
  }
  const viewSize = modules.size + quietZone * 2
  return (
    <Svg
      width={size}
      height={size}
      viewBox={`${-quietZone} ${-quietZone} ${viewSize} ${viewSize}`}
    >
      <Path d={path} fill="#000" />
    </Svg>
  )
}

function SortCode({ value }: { value: string }) {
  const parts = value.split('-')
  return (
    <View style={styles.sortCode}>
      <View style={styles.sortCodeParts}>
        {parts.length === 3 ? (
          <>
            <Text style={styles.sortCodeSmall}>{parts[0]}-</Text>
            <Text style={styles.sortCodeLarge}>{parts[1]}</Text>
            <Text style={styles.sortCodeSmall}>-{parts[2]}</Text>
          </>
        ) : (
          <Text style={styles.sortCodeLarge}>{value}</Text>
        )}
      </View>
    </View>
  )
}

function maskPhone(phone?: string) {
  return (phone ?? '')
    .split(/[,/]/)
    .map((part) => {
      const digits = part.replace(/\D/g, '')
      if (digits.length < 6) {
        return part.trim()
      }
      return `${digits.slice(0, 3)}${'*'.repeat(
        digits.length - 5,
      )}${digits.slice(-2)}`
    })
    .filter(Boolean)
    .join(', ')
}

function itemsText(items: IOrderItem[], showItemInPack?: boolean) {
  return items
    .map((item: IOrderItem) => {
      let message = `${item.productName} ${item.quantity} ${
        item.isCollection ? 'เซต' : 'ชิ้น'
      } ${item?.meta?.size ? `ขนาด ${item.meta.size}` : ''} ${
        item?.meta?.color ? `สี${item.meta.color}` : ''
      }`.trim()
      if (item.isCollection && item.collections?.length && showItemInPack) {
        message += ` [${item.collections
          .map(
            (collection) =>
              `${collection.productName} ${collection.quantity} ชิ้น`,
          )
          .join(', ')}]`
      }
      return message
    })
    .join(', ')
}

function itemName(item: IOrderItem) {
  return [
    item.productName,
    item?.meta?.size ? `ขนาด ${item.meta.size}` : '',
    item?.meta?.color ? `สี${item.meta.color}` : '',
  ]
    .filter(Boolean)
    .join(' ')
}

function ItemsTable({
  items,
  showItemInPack,
}: {
  items: IOrderItem[]
  showItemInPack?: boolean
}) {
  return (
    <View style={tableStyles.table}>
      <View style={tableStyles.headerRow} fixed>
        <Text style={[tableStyles.header, tableStyles.index]}>ลำดับ</Text>
        <Text
          style={[tableStyles.header, tableStyles.cellBorder, tableStyles.code]}
        >
          รหัสสินค้า
        </Text>
        <Text
          style={[tableStyles.header, tableStyles.cellBorder, tableStyles.name]}
        >
          ชื่อสินค้า
        </Text>
        <Text
          style={[
            tableStyles.header,
            tableStyles.cellBorder,
            tableStyles.quantity,
          ]}
        >
          จำนวน
        </Text>
      </View>
      {items.map((item: IOrderItem, itemIndex: number) => (
        <View key={`item-${itemIndex}`}>
          <View style={tableStyles.row} wrap={false}>
            <Text style={[tableStyles.cell, tableStyles.index]}>
              {itemIndex + 1}
            </Text>
            <Text
              style={[
                tableStyles.cell,
                tableStyles.cellBorder,
                tableStyles.code,
              ]}
            >
              {item.productCode}
            </Text>
            <Text
              style={[
                tableStyles.cell,
                tableStyles.cellBorder,
                tableStyles.name,
              ]}
            >
              {itemName(item)}
            </Text>
            <Text
              style={[
                tableStyles.cell,
                tableStyles.cellBorder,
                tableStyles.quantity,
              ]}
            >
              {numberFormat(item.quantity)} {item.isCollection ? 'เซต' : ''}
            </Text>
          </View>
          {showItemInPack &&
            item.isCollection &&
            item.collections?.map((collection, collectionIndex: number) => (
              <View
                key={`collection-${itemIndex}-${collectionIndex}`}
                style={tableStyles.row}
                wrap={false}
              >
                <Text style={[tableStyles.cell, tableStyles.index]} />
                <Text
                  style={[
                    tableStyles.cell,
                    tableStyles.cellBorder,
                    tableStyles.code,
                  ]}
                >
                  {collection.productCode}
                </Text>
                <Text
                  style={[
                    tableStyles.cell,
                    tableStyles.cellBorder,
                    tableStyles.name,
                  ]}
                >
                  - {collection.productName}
                </Text>
                <Text
                  style={[
                    tableStyles.cell,
                    tableStyles.cellBorder,
                    tableStyles.quantity,
                  ]}
                >
                  {numberFormat(collection.quantity)}
                </Text>
              </View>
            ))}
        </View>
      ))}
    </View>
  )
}

function noteText(order: IOrder, showItemInPack?: boolean) {
  return `หมายเหตุ: ${order.note ? `${order.note} ` : ''}${itemsText(
    order.items,
    showItemInPack,
  )}`
}

function recipientNameFontSize(name: string) {
  return name.length > 45 ? 12 : 13
}

function recipientAddressFontSize(address: string) {
  if (address.length > 140) {
    return 10
  }
  return address.length > 110 ? 11 : 12
}

function sortingLineFontSize(code: string) {
  return code.length > 3 ? 40 : 42
}

function LabelSection({
  order,
  label,
  printedAt,
  note,
  showFullPhone,
}: {
  order: IOrder
  label: IFlashTrackingLabel
  printedAt: string
  note?: string
  showFullPhone?: boolean
}) {
  const { sender, recipient } = order
  const recipientPhone = showFullPhone
    ? recipient.phone
    : maskPhone(recipient.phone)
  const recipientName = `ผู้รับ ${recipient.name} ${recipientPhone ?? ''}`
  const recipientAddress = `${recipient.address} ต.${recipient.subDistrict} อ.${recipient.district} จ.${recipient.province} ${recipient.zipcode}`
  const isLongRecipient =
    recipientName.length > 45 || recipientAddress.length > 90
  return (
    <View wrap={false}>
      <SortCode value={label.sortCode} />
      <View style={styles.barcode}>
        <BarCode value={label.pno} />
      </View>
      <Text style={styles.pno}>{label.pno}</Text>
      <View style={styles.box}>
        <View style={styles.dst}>
          <Text style={styles.dstLabel}>DST</Text>
          <Text style={styles.dstName}>{label.dstStoreName}</Text>
        </View>
        <View style={styles.body}>
          <View style={styles.main}>
            <View style={styles.addresses}>
              <Text
                style={[styles.sender, { maxLines: isLongRecipient ? 1 : 2 }]}
              >
                ผู้ส่ง {sender?.name} {sender?.phone} {sender?.address}{' '}
                {sender?.subDistrict} {sender?.district} {sender?.province}{' '}
                {sender?.zipcode}
              </Text>
              <Text
                style={[
                  styles.recipientName,
                  { fontSize: recipientNameFontSize(recipientName) },
                ]}
              >
                {recipientName}
              </Text>
              <Text
                style={[
                  styles.recipientAddress,
                  { fontSize: recipientAddressFontSize(recipientAddress) },
                ]}
              >
                {recipientAddress}
              </Text>
              {!!note && (
                <Text
                  style={[
                    styles.labelNote,
                    { maxLines: isLongRecipient ? 2 : 3 },
                  ]}
                >
                  {note}
                </Text>
              )}
            </View>
            <View style={styles.footer}>
              {order.paymentMethod === EOrderType.COD && (
                <View style={styles.cod}>
                  <Text style={styles.codText}>COD</Text>
                </View>
              )}
              <View style={styles.orderInfo}>
                <Text style={styles.orderCode}>ORDER: {order.code}</Text>
                <Text style={styles.printedAt}>Print: {printedAt}</Text>
              </View>
            </View>
          </View>
          <View style={styles.side}>
            <View style={styles.sortingLine}>
              <Text
                style={[
                  styles.sortingLineText,
                  { fontSize: sortingLineFontSize(label.sortingLineCode) },
                ]}
              >
                {label.sortingLineCode}
              </Text>
            </View>
            <QrCode value={label.pno} size={43} />
          </View>
        </View>
      </View>
    </View>
  )
}

export const PDFFlash = (props: IPDFFlashProps) => {
  const {
    orders,
    labels,
    size,
    showFullPhone,
    showItemInPack,
    showItemAsTable,
  } = props
  const printedAt = dayjs().format(ONLY_DATE_TIME_FORMAT)
  return (
    <Document>
      {orders
        .filter((order: IOrder) => labels[order.objectId])
        .map((order: IOrder) => {
          const label = labels[order.objectId]
          return (
            <Page
              key={order.objectId}
              size={PAGE_SIZES[size]}
              style={styles.page}
            >
              <View
                fixed
                render={({ subPageNumber }) =>
                  subPageNumber > 1 && (
                    <View style={styles.continuation}>
                      <Text
                        style={styles.continuationTitle}
                        render={({ subPageNumber, subPageTotalPages }) =>
                          `รายการสินค้า (ต่อ) ${subPageNumber}/${subPageTotalPages}`
                        }
                      />
                      <Text style={styles.continuationRef}>
                        {label.pno} · ORDER: {order.code}
                      </Text>
                    </View>
                  )
                }
              />
              <LabelSection
                order={order}
                label={label}
                printedAt={printedAt}
                note={
                  size === 'small' ? noteText(order, showItemInPack) : undefined
                }
                showFullPhone={showFullPhone}
              />
              {size === 'large' && showItemAsTable && (
                <>
                  {!!order.note && (
                    <Text style={styles.note}>หมายเหตุ: {order.note}</Text>
                  )}
                  <ItemsTable
                    items={order.items}
                    showItemInPack={showItemInPack}
                  />
                </>
              )}
              {size === 'large' && !showItemAsTable && (
                <Text style={styles.note}>
                  {noteText(order, showItemInPack)}
                </Text>
              )}
            </Page>
          )
        })}
    </Document>
  )
}
